import type { AdmissionField, AdmissionFormSection } from '../../../types/admissions';
import dateTimeUtils from '../../../utils/dateTimeUtils';
import { cnicDigits, formatCnic } from '../../../utils/cnic';
import { fmtClassName } from '../../../utils/pdf/format';
import { isEmailAddress } from '../../../utils/emailAddress';

/**
 * Canonical registry of "system" field keys. A system field is one the
 * backend expects at a well-known slot on the AdmissionApplication payload
 * (or feeds into Person/Guardian creation). Operators can toggle its
 * `enabled` / `required` flags in the template, but the key, label meaning,
 * and canonical destination are frozen — so every template renders the same
 * applicant-identity surface across the public form, the walk-in dialog,
 * and the backend validators.
 *
 * Custom fields (anything NOT in this map) always flow into
 * `AdmissionApplication.formData[key]`.
 */

export type SystemFieldKey =
  // ─── Applicant identity ─────────────────────────────────────────────────
  | 'name' | 'photo' | 'cnic' | 'gender' | 'dob'
  | 'candidateContact' | 'email' | 'address'
  // ─── Admission choice ───────────────────────────────────────────────────
  | 'classId'
  // ─── Previous schooling ────────────────────────────────────────────────
  | 'previousSchool' | 'previousPercentage'
  // ─── Father / primary guardian ──────────────────────────────────────────
  | 'fatherName' | 'fatherPhone' | 'fatherCnic' | 'fatherEmail' | 'fatherOccupation'
  // ─── Mother / secondary guardian ───────────────────────────────────────
  | 'motherName' | 'motherPhone' | 'motherCnic' | 'motherEmail' | 'motherOccupation';

export const SYSTEM_FIELD_KEYS: ReadonlySet<SystemFieldKey> = new Set<SystemFieldKey>([
  'name', 'photo', 'cnic', 'gender', 'dob',
  'candidateContact', 'email', 'address',
  'classId',
  'previousSchool', 'previousPercentage',
  'fatherName', 'fatherPhone', 'fatherCnic', 'fatherEmail', 'fatherOccupation',
  'motherName', 'motherPhone', 'motherCnic', 'motherEmail', 'motherOccupation',
]);

/**
 * The built-in fields an application can't be made without (mirror of the
 * backend's REQUIRED_FORM_FIELD_KEYS). Every cycle's form holds them; the
 * form editor won't remove them or make them optional.
 */
export type RequiredSystemFieldKey =
  | 'name' | 'dob' | 'cnic' | 'gender' | 'candidateContact' | 'address' | 'classId'
  | 'fatherName' | 'fatherCnic' | 'fatherPhone';

export const REQUIRED_SYSTEM_FIELD_KEYS: ReadonlySet<RequiredSystemFieldKey> =
  new Set<RequiredSystemFieldKey>([
    'name', 'dob', 'cnic', 'gender', 'candidateContact', 'address', 'classId',
    'fatherName', 'fatherCnic', 'fatherPhone',
  ]);

export const isSystemFieldKey = (k: string): k is SystemFieldKey =>
  SYSTEM_FIELD_KEYS.has(k as SystemFieldKey);

export const isRequiredSystemFieldKey = (k: string): k is RequiredSystemFieldKey =>
  REQUIRED_SYSTEM_FIELD_KEYS.has(k as RequiredSystemFieldKey);

/**
 * The applicant's photo. Saved on the person (not in the application), so it
 * follows them into enrollment. Optional unless the admin marks it required —
 * and even then only the parent's online form insists on it: the desk
 * (walk-in) and the bulk sheet never block on a photo.
 */
export const isPhotoField = (f: Pick<AdmissionField, 'key'>): boolean => f.key === 'photo';

/** The types an admin can give a field, in the form editors. */
export const FIELD_TYPE_OPTIONS: ReadonlyArray<{ value: AdmissionField['type']; label: string }> = [
  { value: 'text',        label: 'Short text' },
  { value: 'textarea',    label: 'Long text' },
  { value: 'number',      label: 'Number' },
  { value: 'email',       label: 'Email' },
  { value: 'phone',       label: 'Phone' },
  { value: 'date',        label: 'Date' },
  { value: 'dropdown',    label: 'Dropdown' },
  { value: 'multiselect', label: 'Multi-select' },
  { value: 'boolean',     label: 'Yes / No' },
];

/** The type choices for one field — the built-in photo keeps its own type. */
export const fieldTypeOptionsFor = (f: Pick<AdmissionField, 'key'>) =>
  isPhotoField(f) ? [...FIELD_TYPE_OPTIONS, { value: 'photo' as const, label: 'Photo' }] : FIELD_TYPE_OPTIONS;

/** The form with the photo never required — for the walk-in desk. */
export const withOptionalPhoto = (sections: AdmissionFormSection[]): AdmissionFormSection[] =>
  sections.map(s => ({ ...s, fields: s.fields.map(f => (isPhotoField(f) ? { ...f, required: false } : f)) }));

/** The form without the photo — the bulk sheet has no way to carry one. */
export const withoutPhoto = (sections: AdmissionFormSection[]): AdmissionFormSection[] =>
  sections.map(s => ({ ...s, fields: s.fields.filter(f => !isPhotoField(f)) }));

/** Family details not asked again for someone already on file — the family is known. */
export const ON_FILE_FAMILY_FIELDS: ReadonlySet<string> = new Set([
  'fatherName', 'fatherCnic', 'fatherEmail', 'fatherOccupation',
  'motherName', 'motherPhone', 'motherCnic', 'motherEmail', 'motherOccupation',
]);

/** System fields that hold a 13-digit NADRA CNIC / B-Form number. */
const CNIC_FIELD_KEYS: ReadonlySet<SystemFieldKey> = new Set<SystemFieldKey>(['cnic', 'fatherCnic', 'motherCnic']);

export const isCnicFieldKey = (k: string): boolean => CNIC_FIELD_KEYS.has(k as SystemFieldKey);

/** A dropdown / multi-select whose choices the admin types in. Built-in ones
 *  ("Applying for class") take their choices from the cycle instead. */
export const takesOwnOptions = (f: Pick<AdmissionField, 'type' | 'key'>): boolean =>
  (f.type === 'dropdown' || f.type === 'multiselect') && !isSystemFieldKey(f.key);

/** An enabled field that needs choices and has none — blocks saving the form. */
export const isMissingOptions = (f: AdmissionField): boolean =>
  f.enabled !== false && takesOwnOptions(f) && !(f.options || []).length;

/**
 * The cycle's form as it is actually filled in: enabled fields only,
 * sections and fields in form order, empty sections dropped. `hiddenKeys` removes fields a flow doesn't ask
 * (e.g. the walk-in asks CNIC on its first step). Every surface that walks
 * the form — web forms, review steps, printouts, the applicant drawer —
 * uses this so they always agree.
 */
export const activeFormSections = (
  sections: AdmissionFormSection[] | null | undefined,
  hiddenKeys?: ReadonlySet<string>,
): AdmissionFormSection[] =>
  (sections ?? [])
    .map(s => ({
      ...s,
      fields: s.fields
        .filter(f => f.enabled !== false && !hiddenKeys?.has(f.key))
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    }))
    .filter(s => s.fields.length > 0)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/**
 * Split a flat values dictionary into the top-level AdmissionApplication
 * payload shape and the `formData` bag. Numeric string values on
 * `previousPercentage` are coerced to numbers; CNICs are digits-only.
 */
export const splitFormValues = (
  values: Record<string, any>,
  template: { sections?: AdmissionFormSection[] },
): { topLevel: Record<string, any>; formData: Record<string, any> } => {
  const topLevel: Record<string, any> = {};
  const formData: Record<string, any> = {};
  for (const section of template.sections || []) {
    for (const field of section.fields || []) {
      if (field.enabled === false || isPhotoField(field)) continue;   // the photo is uploaded on its own
      const raw = values[field.key];
      if (raw === undefined || raw === null || raw === '') continue;

      let value: any = raw;
      if (isCnicFieldKey(field.key)) {
        value = cnicDigits(String(raw));
      } else if (field.type === 'number') {
        const n = Number(raw);
        value = Number.isFinite(n) ? n : undefined;
      } else if (field.type === 'boolean') {
        value = raw === true || raw === 'true';
      } else if (typeof raw === 'string') {
        value = raw.trim();
        if (value === '') continue;
      }

      if (isSystemFieldKey(field.key)) {
        topLevel[field.key] = value;
      } else {
        formData[field.key] = value;
      }
    }
  }
  return { topLevel, formData };
};

/** Phone fields — by key for templates saved before the `phone` type existed. */
const PHONE_FIELD_KEYS: ReadonlySet<string> = new Set(['candidateContact', 'fatherPhone', 'motherPhone']);
export const isPhoneField = (f: AdmissionField): boolean => f.type === 'phone' || PHONE_FIELD_KEYS.has(f.key);

/** Pakistani mobile or landline: 0XXXXXXXXX(X) or (+)92XXXXXXXXX(X).
 *  Spaces, dashes and brackets are ignored. */
export const isValidPhone = (raw: string): boolean => {
  const v = raw.replace(/[\s\-()]/g, '');
  return /^0\d{9,10}$/.test(v) || /^\+?92\d{9,10}$/.test(v);
};

/** "03001234567" → "0300 1234567", for reading out on a call. Anything else as given. */
export const formatPhone = (raw: string): string => {
  const v = raw.replace(/[\s\-()]/g, '');
  return /^0\d{10}$/.test(v) ? `${v.slice(0, 4)} ${v.slice(4)}` : raw;
};

/** A real calendar date in yyyy-mm-dd (as the date inputs produce). */
export const isValidIsoDate = (raw: string): boolean => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (!m) return false;
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return d.getUTCFullYear() === +m[1] && d.getUTCMonth() === +m[2] - 1 && d.getUTCDate() === +m[3];
};

/** CNIC pairs that must belong to different people. */
const DISTINCT_CNICS: Array<[string, string, string]> = [
  ['fatherCnic', 'cnic', "Can't be the same as the applicant's CNIC"],
  ['motherCnic', 'cnic', "Can't be the same as the applicant's CNIC"],
  ['motherCnic', 'fatherCnic', "Can't be the same as the father's CNIC"],
];

/**
 * The one validator for every admission form — walk-in dialog, public apply
 * page and bulk upload. Returns `fieldKey → message` for each problem:
 *   - required answers (system-required fields are always required; any
 *     other field the admin marked required in the form settings too)
 *   - CNIC (13 digits, and the applicant / father / mother all different)
 *   - phone, email, real dates, date of birth not in the future
 *   - numbers within min / max, text within min / max length or pattern
 *   - dropdown / multi-select answers that are one of the listed options
 */
export const validateDynamicFields = (
  values: Record<string, any>,
  sections: AdmissionFormSection[],
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const today = dateTimeUtils.getTodayInTimezone();
  const present = new Set<string>();

  for (const section of sections) {
    for (const field of section.fields || []) {
      if (field.enabled === false) continue;
      present.add(field.key);
      const raw = values[field.key];
      const empty = raw === undefined || raw === null || (typeof raw === 'string' && raw.trim() === '') ||
        (Array.isArray(raw) && raw.length === 0);
      if (field.required && empty) {
        errors[field.key] = 'Required';
        continue;
      }
      if (empty) continue;
      const text = typeof raw === 'string' ? raw.trim() : String(raw);
      const rules = field.validation;

      if (isCnicFieldKey(field.key)) {
        if (cnicDigits(text).length !== 13) errors[field.key] = 'CNIC must be 13 digits';
      } else if (isPhoneField(field)) {
        if (!isValidPhone(text)) errors[field.key] = 'Enter a valid phone number, e.g. 0300 1234567';
      } else if (field.type === 'email') {
        if (!isEmailAddress(text)) errors[field.key] = 'Enter a valid email address';
      } else if (field.type === 'date') {
        if (!isValidIsoDate(text)) errors[field.key] = 'Enter a valid date';
        else if (field.key === 'dob' && text.slice(0, 10) > today) errors[field.key] = "Date of birth can't be in the future";
      } else if (field.type === 'number') {
        const n = Number(text);
        if (!Number.isFinite(n)) errors[field.key] = 'Enter a number';
        else if (rules?.min !== undefined && n < rules.min) errors[field.key] = `Must be at least ${rules.min}`;
        else if (rules?.max !== undefined && n > rules.max) errors[field.key] = `Must be at most ${rules.max}`;
      } else if ((field.type === 'dropdown' || field.type === 'multiselect') && field.key !== 'classId' && field.options?.length) {
        const allowed = new Set(field.options.map(o => o.value));
        const picked = Array.isArray(raw) ? raw.map(String) : [text];
        if (picked.some(v => !allowed.has(v))) errors[field.key] = 'Choose one of the listed options';
      } else if (field.type === 'text' || field.type === 'textarea') {
        if (rules?.minLength !== undefined && text.length < rules.minLength) errors[field.key] = `Must be at least ${rules.minLength} characters`;
        else if (rules?.maxLength !== undefined && text.length > rules.maxLength) errors[field.key] = `Must be at most ${rules.maxLength} characters`;
        else if (rules?.pattern && !safeRegex(rules.pattern)?.test(text)) errors[field.key] = 'Not in the expected format';
      }
    }
  }

  for (const [key, other, message] of DISTINCT_CNICS) {
    if (!present.has(key) || errors[key] || !values[key] || !values[other]) continue;
    if (cnicDigits(String(values[key])) === cnicDigits(String(values[other]))) errors[key] = message;
  }
  return errors;
};

/** Admin-entered patterns are compiled defensively — a malformed pattern
 *  must not break the form, so it simply isn't enforced. */
const safeRegex = (pattern: string): RegExp | null => {
  try { return new RegExp(pattern); } catch { return null; }
};

/**
 * Human-readable rendering of one answer — option labels instead of codes,
 * class name instead of id, formatted dates and CNICs. '' when unanswered.
 * Use anywhere a submitted form is read back (review steps, summaries).
 */
export const fieldDisplayValue = (
  field: AdmissionField,
  value: unknown,
  classOptions: Array<{ _id: string; name: string }> = [],
): string => {
  if (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)) return '';
  if (isPhotoField(field)) return '';
  if (field.key === 'classId') return fmtClassName(classOptions.find(c => c._id === value)?.name);
  if (isCnicFieldKey(field.key)) return formatCnic(String(value));
  const optionLabel = (v: unknown) => field.options?.find(o => o.value === v)?.label ?? String(v);
  switch (field.type) {
    case 'dropdown':    return optionLabel(value);
    case 'multiselect': return (value as unknown[]).map(optionLabel).join(', ');
    case 'boolean':     return value === true || value === 'true' ? 'Yes' : 'No';
    case 'date':        return dateTimeUtils.formatDateForDisplay(String(value));
    default:            return String(value);
  }
};
